import { useAchievementTypes, useReduxState } from 'hooks';
import {
  ContentValue,
  ContentLabel,
  User,
  ModalButton,
  DisplayFormattedDate,
  DisplayAttachments,
  TextWithLineBreaks
} from 'components/common';
import { AttributeDisplayValue } from 'components/common/customAttributes';

import type { GetTeamPendingRequestsRes } from 'shared-types/responses';

type AchievementRequest = Extract<
  GetTeamPendingRequestsRes['requests'][number]['data'],
  { type: 'achievement' }
>;

interface Props {
  achievement: AchievementRequest['achievement'];
  data: AchievementRequest['requestData'];
  user: GetTeamPendingRequestsRes['requests'][number]['user'];
}

const MyTeamPendingRequestsViewAchievement = ({
  achievement,
  data,
  user
}: Props) => {
  const { language, messages } = useReduxState();

  const achievementTypes = useAchievementTypes();

  return (
    <ModalButton
      category="lighter"
      isRounded={true}
      modalProps={{
        closeButton: true,
        title: achievement.title,
        width: 'medium'
      }}
      size="small"
      style={{ padding: '4px 9px 5px' }}
      text={messages.commonViewAction}
    >
      <ContentLabel horizontal={true} label={messages.commonUser} spacing={15}>
        <User user={user} enableProfileLink={true} />
      </ContentLabel>

      <ContentLabel horizontal={true} label={messages.commonType} spacing={15}>
        <ContentValue>{achievementTypes[achievement.type].title}</ContentValue>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.commonStartDate}
        spacing={15}
      >
        <ContentValue>
          <DisplayFormattedDate date={data.startedAtDate} />
        </ContentValue>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.completionsCompletionDate}
        spacing={15}
      >
        <ContentValue>
          <DisplayFormattedDate date={data.completionDate} />
        </ContentValue>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.commonExpirationDate}
        spacing={15}
      >
        <ContentValue>
          <DisplayFormattedDate date={data.expirationDate} />
        </ContentValue>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.commonAttachments}
        spacing={15}
      >
        <ContentValue>
          <DisplayAttachments attachments={data.attachments || []} />
        </ContentValue>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.commonComment}
        spacing={15}
      >
        <ContentValue>
          <TextWithLineBreaks text={data.comments} />
        </ContentValue>
      </ContentLabel>

      {achievement.userAttributes?.map((attribute) => {
        const value = data?.userAttributes?.[attribute.id];
        return (
          <ContentLabel
            key={attribute.id}
            horizontal={true}
            label={attribute.titleTranslations?.[language] || attribute.title}
            spacing={15}
          >
            <ContentValue>
              <AttributeDisplayValue value={value || '-'} />
            </ContentValue>
          </ContentLabel>
        );
      })}
    </ModalButton>
  );
};

export default MyTeamPendingRequestsViewAchievement;
